import { useDispatch } from "react-redux";
import { useState } from "react";
import { ICreateConversationRequest } from "Common/Interfaces/Domain/Conversations";
import { IRawResponse } from "Common/Interfaces/Domain/Conversations/IRawResponse";
import { getAccessToken } from "../AuthenticationHelper";
import { setConversationLoading } from "../Slice";

const useConversationStream = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);

  const createConversationStream = async (
    conversationData: ICreateConversationRequest,
    onChunkReceived: (data: IRawResponse | string) => void
  ) => {
    try {
      dispatch(setConversationLoading(true)); // Set loading state to true

      const token = await getAccessToken();
      const response = await fetch(`${import.meta.env.VITE_ASKAMI_API_BASE_URL}conversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Ocp-Apim-Subscription-Key": import.meta.env.VITE_SUBSCRIPTION_KEY ?? "",
        },
        body: JSON.stringify(conversationData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create conversation. Status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = ""; // To accumulate data between chunks
      let accumulatedChunks: (IRawResponse | string)[] = []; // Array to accumulate responses
      let updateTimer: NodeJS.Timeout | null = null;
      let isFirstChunk = true; // Track if this is the first chunk for instant feedback

      // Function to flush accumulated data after a certain interval
      const flushBufferPeriodically = () => {
        if (accumulatedChunks.length > 0) {
          // Call onChunkReceived for each accumulated chunk
          accumulatedChunks.forEach((chunk) => onChunkReceived(chunk));
          accumulatedChunks = []; // Reset the buffer
        }
      };

      // Process the stream
      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split messages based on newlines, but keep the last one in the buffer in case it's incomplete
        const messages = buffer.split("\n");
        buffer = messages.pop() ?? ""; // Reassign incomplete chunk back to buffer

        for (const message of messages) {
          if (message.startsWith("data: ")) {
            const jsonString = message.substring(6); // Remove "data: " from the start

            try {
              const parsedData = JSON.parse(jsonString);

              if (parsedData.type === "title_generation") {
                accumulatedChunks.push(parsedData.title); // Store title generation data
              } else {
                const responseMessage: IRawResponse = parsedData;
                accumulatedChunks.push(responseMessage); // Store other response messages
              }

              // For first chunk, flush immediately for instant feedback
              if (isFirstChunk) {
                isFirstChunk = false;
                flushBufferPeriodically();
              } else {
                // Set or reset the timer for periodic state updates (every 105ms for better responsiveness)
                if (updateTimer) clearTimeout(updateTimer);
                updateTimer = setTimeout(flushBufferPeriodically, 105);
              }
            } catch (error) {
              console.error("Error parsing message chunk:", error);
            }
          }
        }
      }

      // Clear any pending timer and flush remaining chunks
      if (updateTimer) {
        clearTimeout(updateTimer);
        updateTimer = null;
      }
      if (accumulatedChunks.length > 0) {
        flushBufferPeriodically();
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(`Failed to create conversation: ${err.message}`);
        console.error("Failed to create conversation:", err.message);
        throw err;
      } else {
        setError("An unexpected error occurred. Reload and try again later.");
        console.error("An unexpected error occurred:", err);
        throw err;
      }
    } finally {
      dispatch(setConversationLoading(false)); // Set loading state to false
    }
  };

  return { createConversationStream, error };
};

export default useConversationStream;
